import React, { useContext, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useClickAway } from 'ahooks'

import { DashboardContext } from 'live/context/dashboard'
import ActionToggle from 'live/components/ui/action-toggle'
import HelpTip from 'live/components/ui/help-tip'
import i18n from 'live/services/i18n'
import theme from 'live/services/theme'
import SpanInput from 'live/components/time/ui/span-input'
import xhr from 'live/services/xhr'
import dashboardService from 'live/services/dashboard'
import LiveSelect from 'live/components/forms/ui/select'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'

import { defaultConfigs, runTypeOptions } from '../utils/initialStates'
import { get } from 'lodash'
import { getRunTypeOptions } from '../utils/utils'

export const StyledConfigs = styled.div<{ dark: boolean }>`
    position: absolute;
    cursor: default;
    top: 26px;
    left: 0px;
    min-width: 400px;
    border-radius: 0;
    background-color: ${props => (props.dark ? 'var(--gray-1000)' : 'var(--gray-100)')};
    margin: 8px 0px 0px 0px;
    border: 1px solid ${props => (props.dark ? theme['darker-bg'] : 'var(--gray-400)')};
    box-shadow: 0 0 5px 0 rgba(96, 105, 128, 0.25);
    z-index: 100;
    color: ${props => (props.dark ? theme.white : 'var(--gray-700)')};

    > .styled-configs-content {
        height: calc(100vh - 250px);
        overflow-y: auto;
    }

    h3 {
        font-size: 14px;
        font-family: ${({ theme }) => theme['font-semibold']};
        padding: 16px 27px;
        margin-bottom: 6px;
        background-color: ${props => (props.dark ? theme.white : 'var(--gray-0)')}; //ToDo: props.dark
        color: ${props => (props.dark ? theme.white : 'var(--gray-700)')};
    }
    h4,
    ul {
        padding: 6px 27px;
        color: ${props => (props.dark ? theme.white : 'var(--gray-700)')};
    }
    span,
    ul > li.toggle {
        color: ${props => (props.dark ? theme.white : 'var(--gray-700)')};
    }

    li.toggle {
        display: flex;
        margin: 0px 0px 8px;
        font-family: ${theme['font-semibold']};
    }

    li.default-span,
    li.dashboard-span {
        width: 100%;
        font-family: ${theme['font-semibold']};
    }

    li.default-span {
        input {
            height: 35px;
            box-shadow: none;
        }
        > div {
            margin-top: 8px; 
        }
        div > div {
            border: none;
        }
    }

    li.dashboard-span {
        display: flex;
        flex-flow: column;
    }

    div.form-group {
        margin-bottom: 10px;
        label {
            font-family: ${theme['font-semibold']};
            font-size: 12px;
            color: ${props => (props.dark ? theme.white : 'var(--gray-700)')};
        }
    }

    .choose-standard-title {
        margin-bottom: 10px;

        .choose-standard-title-label {
            font-size: 1.3rem;
            font-family: ${theme['font-semibold']};
        }
    }

    .radio-inline {
        padding-left: 0px;
        padding: 6px 30px 0px 0px;
    }

    .action-toggle {
        top: 0;
        ${({ theme }) => (theme['black-scale'] ? `background-color: ${theme['dash-background']}` : '')};
    }
`

const ConfigActions = styled.div<{ dark: boolean }>`
    margin-top: 6px;
    border-top: 1px solid ${props => (props.dark ? 'var(--gray-1000)' : 'var(--gray-400)')};
    padding: 10px 27px;
    button {
        height: auto !important;
    }
`

export function validatePipeExpression(expression: string) {
    return xhr.post('/rest/span', {
        expression: expression
    })
}

export function customDatesExpression(sd, ed, st, et) {
    let startTime = ''
    let endTime = ''
    const r = st.match(/(\d\d):(\d\d)/)
    const s = et && et.match(/(\d\d):(\d\d)/)
    const f = 'YYYY-MM-DD'

    if (r && r.length > 1 && r[1] >= 0 && r[1] < 24 && r[2] >= 0 && r[2] < 60) {
        startTime = st
    }

    if (s && s.length > 1 && s[1] >= 0 && s[1] < 24 && s[2] >= 0 && s[2] < 60) {
        endTime = et
    }

    return sd.format(f) + ' ' + startTime + (ed || et ? ' to ' + ed.format(f) + ' ' + endTime : '')
}

const OperationsPickerConfigPath = dashboardService.getSpanPickerConfigPath('drilling-operations-span-picker')

/**
 * This component configure how the tree will behave (if it will have checkboxes, auto load the last run, default timespan...)
 * @param props
 * @returns {}
 */

const OperationsPickerConfig = props => {
    const { state, params, ...context } = useContext(DashboardContext)
    const ref = useRef(null)
    const spanPickerConfig: OperationsPickerConfigsProps = get(context, 'dashboard.' + OperationsPickerConfigPath, {})
    const [advancedMode, setAdvancedMode] = useState(spanPickerConfig?.advancedMode ?? defaultConfigs?.advancedMode)

    useClickAway(() => {
        props.toggleOpen()
    }, ref)

    const [autoLoadsLastRun, setAutoLoadsLastRun] = useState(
        spanPickerConfig?.autoLoadsLastRun ?? defaultConfigs?.autoLoadsLastRun
    )
    const [autoLoadsLastRunTillNow, setAutoLoadsLastRunTillNow] = useState(
        spanPickerConfig?.autoLoadsLastRunTillNow ?? defaultConfigs.autoLoadsLastRunTillNow
    )
    const [enableChecking, setEnableChecking] = useState(
        spanPickerConfig?.enableChecking ?? defaultConfigs.enableChecking
    )
    const [updateDashboardSpan, setUpdateDashboardSpan] = useState(
        spanPickerConfig?.updateDashboardSpan ?? defaultConfigs.updateDashboardSpan
    )

    const [autoSelectLastRun, setAutoSelectLastRun] = useState(
        spanPickerConfig?.autoSelectLastRun ?? defaultConfigs.autoSelectLastRun
    )

    const [timespanDefaultOption, setTimespanDefaultOption] = useState(
        spanPickerConfig?.timespanDefaultOption ?? defaultConfigs.timespanDefaultOption
    )

    const [autoCheck, setAutoCheck] = useState(spanPickerConfig?.autoCheck ?? defaultConfigs.autoCheck)

    const [autoCheckByRunType, setAutoCheckByRunType] = useState(
        spanPickerConfig?.autoCheckByRunType ?? defaultConfigs.autoCheckByRunType
    )

    const [saveRunReference, setSaveRunReference] = useState(
        spanPickerConfig?.saveRunReference ?? defaultConfigs.saveRunReference
    )

    const [displayOperations, setDisplayOperations] = useState(
        spanPickerConfig?.displayOperations ?? defaultConfigs.displayOperations
    )
    const [onlyDisplayRunsWithOperations, setOnlyDisplayRunsWithOperations] = useState(
        spanPickerConfig?.onlyDisplayRunsWithOperations ?? defaultConfigs.onlyDisplayRunsWithOperations
    )
    const [displayItemLabel, setDisplayItemLabel] = useState(
        spanPickerConfig?.displayItemLabel ?? defaultConfigs.displayItemLabel
    )
    const [restrictDate, setRestrictDate] = useState(spanPickerConfig?.restrictDate ?? defaultConfigs.restrictDate)

    const [selectRunTypeOption, setSelectRunTypeOption] = useState(
        getRunTypeOptions(spanPickerConfig?.defaultRunTypeOption) ?? []
    )
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [defaultSpan, setDefaultSpan] = useState('last hour')

    useEffect(() => {
        if (!autoLoadsLastRun && autoLoadsLastRunTillNow) setAutoLoadsLastRunTillNow(false)
    }, [autoLoadsLastRun])

    useEffect(() => {
        if (autoCheck !== 'lastSection') setAutoCheckByRunType(false)
    }, [autoCheck])

    const applyChanges = (defaultSpan: string): void => {
        setLoading(true)
        validatePipeExpression(defaultSpan)
            .then(() => {
                props.changeSpanPickerConfig({
                    autoLoadsLastRun: autoLoadsLastRun,
                    autoLoadsLastRunTillNow: autoLoadsLastRunTillNow,
                    enableChecking: enableChecking,
                    updateDashboardSpan: updateDashboardSpan,
                    updateWidgetsSpan: !updateDashboardSpan,
                    autoSelectLastRun: autoSelectLastRun,
                    autoCheck: autoCheck,
                    autoCheckByRunType: autoCheckByRunType,
                    timespanDefaultOption: timespanDefaultOption,
                    saveRunReference: saveRunReference,
                    displayOperations: displayOperations,
                    onlyDisplayRunsWithOperations: onlyDisplayRunsWithOperations,
                    displayItemLabel: displayItemLabel,
                    advancedMode: advancedMode,
                    changeSpanPickerConfig: props.changeSpanPickerConfig,
                    defaultSpan: defaultSpan,
                    defaultRunTypeOption: selectRunTypeOption.map(obj => obj.value).join(',')
                })
                props.actions.setTimespan(defaultSpan, 'defaultOption')
                props.toggleOpen()
            })
            .catch(res => {
                setError(res.data)
                setLoading(false)
            })
    }

    return props.open ? (
        <StyledConfigs dark={context.theme === 'dark'} ref={ref}>
            <h3>CONFIGURATIONS</h3>
            <div className="styled-configs-content">
                <h4>First Load</h4>
                <ul>
                    <li className="toggle">
                        {' '}
                        <ActionToggle
                            onChange={() => setAutoLoadsLastRun(!autoLoadsLastRun)}
                            active={autoLoadsLastRun}
                        />
                        {i18n('Loads automatically the last run.')}
                    </li>
                    <li className="toggle">
                        <ActionToggle
                            disabled={!autoLoadsLastRun}
                            onChange={() => setAutoLoadsLastRunTillNow(!autoLoadsLastRunTillNow)}
                            active={autoLoadsLastRunTillNow}
                        />
                        {i18n(' Loads automatically the last run from its begining untill now.')}
                    </li>
                    <li className="dashboard-span">
                        <span>Choose the default set of checked items:</span>
                        <div className="form-group radio-box">
                            <label className={`radio-inline`}>
                                <input
                                    type="radio"
                                    checked={autoCheck === 'none'}
                                    onChange={() => setAutoCheck('none')}
                                    disabled={!enableChecking}
                                />
                                &nbsp;
                                {i18n('None')}
                            </label>
                            <label className={`radio-inline`}>
                                <input
                                    type="radio"
                                    checked={autoCheck === 'lastRun'}
                                    onChange={() => setAutoCheck('lastRun')}
                                    disabled={!enableChecking}
                                />
                                &nbsp;
                                {i18n('Last Run')}
                            </label>
                            <label className={`radio-inline`}>
                                <input
                                    type="radio"
                                    checked={autoCheck === 'lastSection'}
                                    onChange={() => setAutoCheck('lastSection')}
                                    disabled={!enableChecking}
                                />
                                &nbsp;
                                {i18n('Last Section')}
                            </label>
                        </div>
                    </li>
                    <li className="toggle">
                        <ActionToggle
                            onChange={() => setAutoCheckByRunType(!autoCheckByRunType)}
                            active={autoCheckByRunType}
                            disabled={autoCheck !== 'lastSection'}
                        />
                        {i18n('Autochecks only runs of the same type of the last started run.')}
                    </li>
                    <li className="dashboard-span">
                        <span>Choose a default way of collecting the checked timespans:</span>
                        <div className="form-group radio-box">
                            <label className={`radio-inline`}>
                                <input
                                    type="radio"
                                    checked={timespanDefaultOption === 'individual'}
                                    onChange={() => setTimespanDefaultOption('individual')}
                                    disabled={!enableChecking}
                                />
                                &nbsp;
                                {i18n('Individual')}
                            </label>
                            <label className={`radio-inline`}>
                                <input
                                    type="radio"
                                    checked={timespanDefaultOption === 'minMax'}
                                    onChange={() => setTimespanDefaultOption('minMax')}
                                    disabled={!enableChecking}
                                />
                                &nbsp;
                                {i18n('Min and Max timespans')}
                            </label>
                        </div>
                    </li>
                </ul>
                <h4>Display Options</h4>
                <ul>
                    <li className="toggle">
                        <ActionToggle
                            onChange={() => setDisplayOperations(!displayOperations)}
                            active={displayOperations}
                        />
                        {i18n('Display the operations inside the tree.')}
                    </li>
                    <li className="toggle">
                        <ActionToggle
                            onChange={() => setOnlyDisplayRunsWithOperations(!onlyDisplayRunsWithOperations)}
                            active={onlyDisplayRunsWithOperations}
                        />
                        {i18n('Only display runs with operations.')}
                    </li>
                    <li className="toggle">
                        <ActionToggle
                            onChange={() => setDisplayItemLabel(!displayItemLabel)}
                            active={displayItemLabel}
                        />
                        {i18n("Display the name of the run/operation on the span's display.")}
                    </li>
                </ul>

                <h4>General Options</h4>
                <ul>
                    <li className="toggle">
                        <ActionToggle onChange={() => setEnableChecking(!enableChecking)} active={enableChecking} />
                        {i18n('Enable checking.')}
                        <HelpTip
                            id="compress-tip"
                            placement="top"
                            text={i18n(
                                'It uses the identity lookup to pass the timespan of the checked values to the dashboard'
                            )}
                        />
                    </li>
                    <li className="toggle">
                        <ActionToggle
                            onChange={() => setAutoSelectLastRun(!autoSelectLastRun)}
                            active={autoSelectLastRun}
                        />
                        {i18n('Auto selects the last run.')}
                    </li>

                    <li className="toggle">
                        <ActionToggle
                            onChange={() => setSaveRunReference(!saveRunReference)}
                            active={saveRunReference}
                        />
                        {i18n('Saves a reference of the run to be used in pipes macros.')}
                    </li>
                    <li className="dashboard-span">
                        <span>Choose a way of updating the dashboard span:</span>
                        <div className="form-group radio-box">
                            <label className={`radio-inline`}>
                                <input
                                    type="radio"
                                    checked={updateDashboardSpan}
                                    onChange={() => setUpdateDashboardSpan(true)}
                                />
                                &nbsp;
                                {i18n('All the dashboard')}
                            </label>
                            <label className={`radio-inline`}>
                                <input
                                    type="radio"
                                    checked={!updateDashboardSpan}
                                    onChange={() => setUpdateDashboardSpan(false)}
                                />
                                &nbsp;
                                {i18n('Widget by widget')}
                            </label>
                        </div>
                    </li>
                    <li>
                        <div className="select-input" style={{ width: '100%' }}>
                            <LiveSelect
                                placeholder="All Operations"
                                options={runTypeOptions}
                                onChange={(value, valueObj) => setSelectRunTypeOption(valueObj)}
                                value={selectRunTypeOption}
                                multi={true}
                            />
                        </div>
                    </li>
                    <li className="toggle">
                        <ActionToggle onChange={() => setRestrictDate(!restrictDate)} active={restrictDate} />
                        {i18n('Restrict date on span picker.')}
                    </li>

                    <li className="toggle">
                        <ActionToggle onChange={() => setAdvancedMode(!advancedMode)} active={advancedMode} />
                        {i18n('Start with advanced mode.')}
                    </li>
                    <li className="default-span">
                        Default span
                        <SpanInput
                            type="text"
                            onChange={e => {
                                setError(null)
                                setDefaultSpan(e.target.value)
                            }}
                            value={defaultSpan}
                            hasError={typeof error?.message != 'undefined'}
                            autoFocus
                            disabled={false}
                            isBelow
                        />
                    </li>
                </ul>
            </div>
            <ConfigActions dark={context.theme === 'dark'}>
                <SubmitOrCancel
                    btnsClassName="btn-lg btn-bold"
                    saving={loading}
                    onSubmit={() => applyChanges(defaultSpan)}
                    onCancel={props.toggleOpen}
                />
            </ConfigActions>
        </StyledConfigs>
    ) : null
}

const HOC = (Component) => (props) => {
    if(props?.open) return <Component {...props} />
    return <></>
}

export default HOC(OperationsPickerConfig)
